import Icon from "../../components/ui/Icon";
import { FC, useContext, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import useInitializer from "../../hooks/useInitializer";
import useListFilters from "../../hooks/useListFilters";
import { formatBoolean, formatTimeStamp } from "../../utils";

import {
  AddIcon,
  Button,
  FlexContent,
  HistoryIcon,
  ModalDialog,
  TableCell,
  TableHeadCell,
  TableRow,
  useDebounce,
} from "@rothoblaas/design-system";
import Header from "../../components/ui/Header";
import Table from "../../components/ui/Table";
import { getDeleteAction, getEditAction } from "../../utils/rowActions";
import { useAuthContext } from "../../context/authContext";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import useI18n from "../../hooks/useTranslations";
import { isNotFound } from "../../http/errors";
import { SearchAdminItem, SearchItem } from "../../http/types/items";
import { useItemsApi } from "../../http/useItems";
import { PaginationQuery } from "../../types";

const AdminItems: FC = () => {
  const i18n = useI18n();
  const navigate = useNavigate();
  const dispatch = useContext(NotificationDispatch);
  const auth = useAuthContext();
  const filters = useListFilters({ isSearchable: true });
  const aborter = useRef(new AbortController());
  const itemsApi = useItemsApi();
  const debouncedValue = useDebounce(filters.state.q);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { data, error } = itemsApi.useAdminItems(query, aborter.current.signal);

  const [itemToDelete, setItemToDelete] = useState<SearchItem>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isInitialized = useInitializer(data || error);

  if (!data && !error && !isInitialized) {
    return;
  }

  return (
    <>
      <Header
        title={i18n.translation.items.plural}
        path={{ display: i18n.translation.items.all }}
        error={error}
        buttons={
          <Button onClick={() => navigate(`/admin/items/new`)}><Icon glyph={AddIcon} />
            {i18n.translation.common.createNew}
          </Button>
        }
      />

      <FlexContent padding noPaddingTop>
        <Table<SearchAdminItem>
          filters={filters}
          search={{
            placeholder: i18n.translation.items.search,
            onChange: () => {
              aborter.current.abort();
              aborter.current = new AbortController();
            },
          }}
          head={
            <TableRow>
              <TableHeadCell align="left">{i18n.translation.common.code}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.common.description}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.common.automaticSync}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.common.lastSync}</TableHeadCell>
              <TableHeadCell></TableHeadCell>
            </TableRow>
          }
          items={data?.items}
          renderRow={(item) => (
            <>
              <TableCell>
                <Link to={`/admin/items/${item.id}`} onClick={(e) => e.stopPropagation()}>
                  {item.code}
                </Link>
              </TableCell>
              <TableCell>{item.description}</TableCell>
              <TableCell>{formatBoolean(i18n.translation, item.automaticSync)}</TableCell>
              <TableCell>{item.lastSync ? formatTimeStamp(item.lastSync, auth.identity?.language) : undefined}</TableCell>
            </>
          )}
          onRowClick={(item) => navigate(`/admin/items/${item?.id}`)}
          rowActions={(item) => [
            getEditAction(i18n, () => navigate(`/admin/items/${item?.id}`)),
            {
              label: i18n.translation.common.sync,
              onClick: () =>
                itemsApi.syncAdminItem(item?.id ?? "").catch((err) => {
                  const msg = isNotFound(err) ? i18n.translation.items.errors.doesNotExist : undefined;
                  dispatch(showErrorNotification(i18n, err, msg));
                }),
              icon: <Icon glyph={HistoryIcon} />,
            },
            getDeleteAction(i18n, () => setItemToDelete(item), true),
          ]}
          total={data?.total}
          error={!!error}
        />

        {!!itemToDelete && (
          <ModalDialog
            onClose={() => setItemToDelete(undefined)}
            title={i18n.translation.items.delete}
            isSubmitting={isSubmitting}
            buttons={[<Button key={0} onClick={() => setItemToDelete(undefined)} variant="secondary">{i18n.translation.common.cancel}</Button>, <Button key={1} onClick={() => {
                  setIsSubmitting(true);
                  itemsApi
                    .deleteAdminItem(itemToDelete.id)
                    .then(() => setItemToDelete(undefined))
                    .catch((err) => dispatch(showErrorNotification(i18n, err)))
                    .finally(() => setIsSubmitting(false));
                }}>{i18n.translation.common.yes}</Button>]}
          >
            {i18n.translation.items.questions.delete}
          </ModalDialog>
        )}
      </FlexContent>
    </>
  );
};

export default AdminItems;
